/**
 * Control-scheme picker — the blocking A/B/C/D panel, and the cog it collapses
 * into.
 *
 * Two modes. `prompt` (testing) is the flow below. `cog` (production today)
 * skips the opening panel: the shipped scheme is in force from the first
 * frame and the cog is simply there in the toolbar; its panel is the
 * dismissible second-visit kind from the start.
 *
 * Flow (see control-schemes.ts for why this layer exists at all):
 *   splat has painted → panel opens, MODAL: nothing behind it takes input until
 *   a scheme is chosen → it asks for a LANGUAGE first, then shows the schemes
 *   in that language → pick one → panel collapses toward the top-right corner
 *   and the cog fades up in the toolbar row there, first slot, with a ~5 s
 *   callout pointing at it → the tutorial for that scheme runs → the cog
 *   reopens the panel any time (straight to the schemes), and that second
 *   visit is dismissible.
 *
 * WHY A LANGUAGE STEP. Testers all get the same link, and a Spanish or Catalan
 * speaker on an English browser has to be able to read the choices before
 * making one. It is asked once, on the panel's first open; after that a small
 * link under the schemes goes back to it.
 *
 * FEEDBACK is a third step in the same card, behind a button under the
 * schemes once there is something to rate: stars (1–5) for the modes this
 * tester actually entered, a ♥ for one favourite, a comment and an optional
 * name. Untried modes are listed dimmed and switch to that mode on a tap —
 * the answers wait in memory for them to come back.
 *
 * WHY MODAL, AND HOW IT BLOCKS. The experiment only produces comparable answers
 * if every tester makes a deliberate choice, so the first panel has no way past
 * it except choosing. Blocking is belt AND braces because viewer input arrives
 * on three different paths:
 *   - pointer/wheel → the backdrop covers the canvas, so canvas listeners never
 *     see them;
 *   - keys (WASD/arrows) → listeners are on WINDOW, which no overlay covers, so
 *     a capture-phase swallow is needed;
 *   - the virtual joystick → a position:fixed element on document.body at
 *     z-index 20, i.e. OUTSIDE the ui root's stacking context, so it would
 *     paint straight through this panel. It cannot be covered from in here;
 *     mount() pauses the nav controller instead (onOpen/onClose), which
 *     detaches its listeners and removes the joystick entirely.
 *
 * Styles are injected from this file rather than added to viewer.css so the
 * whole experiment stays deletable as one folder.
 */

import { resolveLang, type UiLang } from '../ui/lang';
import {
    COPY, DEFAULT_SCHEME, isTouchDevice, savedScheme, saveScheme, schemeIdsFor,
    type Registry, type SchemeId
} from './control-schemes';
import type { FeedbackAnswers } from './feedback';

const svg = (body: string) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

// One glyph per scheme, per registry — the shape of the GESTURE, not a
// device. Same line weight as the controls hint so the cards read as family.
const GLYPH: Record<Registry, Partial<Record<SchemeId, string>>> = {
    walk: {
        // Classic: a cursor that drags, with travel chevrons behind it.
        A: svg('<path d="M6 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M17.5 15.5l2.5 2.5-2.5 2.5"/><path d="M20 18h-6"/>'),
        // Classic lite: the same cursor as A with the travel chevrons taken off —
        // the mark says "this, with less" without inventing a new metaphor.
        B: svg('<path d="M6 3.5l5 12.5 1.8-5 4.9-1.9z"/>'),
        // Cursor: the same cursor parked against a screen edge, with a nudge
        // toward it — rest there and the view turns that way.
        C: svg('<path d="M5 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M21 4v16"/><path d="M13.5 18.5h4.5"/><path d="M16 16.5l2 2-2 2"/>'),
        // Hybrid: C's cursor at the edge with A's drag chevrons back under it.
        D: svg('<path d="M5 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M21 4v16"/><path d="M8 20h8"/><path d="M10 18l-2 2 2 2"/><path d="M14 18l2 2-2 2"/>'),
        // Gamer: a crosshair — the cursor is gone and the view follows the mouse.
        E: svg('<circle cx="12" cy="12" r="6"/><path d="M12 3v3.5M12 17.5V21M3 12h3.5M17.5 12H21"/>')
    },
    orbit: {
        // Classic: walk A's dragging cursor — same gesture, same mark.
        A: svg('<path d="M6 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M17.5 15.5l2.5 2.5-2.5 2.5"/><path d="M20 18h-6"/>'),
        // WASD: the key cluster from the tutorial card.
        B: svg('<rect x="9" y="3" width="6" height="6" rx="1.3"/><rect x="2.5" y="11.5" width="6" height="6" rx="1.3"/><rect x="9" y="11.5" width="6" height="6" rx="1.3"/><rect x="15.5" y="11.5" width="6" height="6" rx="1.3"/>'),
        // Cursor: walk C's mark — cursor parked against a screen edge.
        C: svg('<path d="M5 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M21 4v16"/><path d="M13.5 18.5h4.5"/><path d="M16 16.5l2 2-2 2"/>'),
        // Cursor & keys: C's edge cursor with a W/S key pair for the zoom.
        D: svg('<path d="M5 3.5l5 12.5 1.8-5 4.9-1.9z"/><path d="M21 4v16"/><rect x="12.5" y="14" width="5.5" height="5.5" rx="1.2"/>'),
        // Spacebar: the wide key with an in/out arrow above it.
        E: svg('<rect x="4" y="15" width="16" height="5.5" rx="1.6"/><path d="M12 3.5v8"/><path d="M9.8 5.7L12 3.5l2.2 2.2"/><path d="M9.8 9.3l2.2 2.2 2.2-2.2"/>'),
        // Scroll: chevrons in all four directions — the scroll gesture steers
        // both axes.
        F: svg('<path d="M9 5.5L12 2.5l3 3"/><path d="M9 18.5l3 3 3-3"/><path d="M5.5 9l-3 3 3 3"/><path d="M18.5 9l3 3-3 3"/>'),
        // Gamer: walk E's crosshair — the cursor is gone, the view follows.
        G: svg('<circle cx="12" cy="12" r="6"/><path d="M12 3v3.5M12 17.5V21M3 12h3.5M17.5 12H21"/>')
    },
    'walk-touch': {
        // Classic: a fingertip swiping (the shipped card's swipe mark).
        A: svg('<circle cx="6.5" cy="12" r="2.3" fill="currentColor" stroke="none"/><path d="M11 12h7"/><path d="M14.8 8.4l3.7 3.6-3.7 3.6"/>'),
        // Gyro: a phone between motion arcs.
        B: svg('<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M3.5 8.5a9 9 0 0 0 0 7"/><path d="M20.5 8.5a9 9 0 0 1 0 7"/>'),
        // Hold: a fingertip with a ring around it.
        C: svg('<circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="7"/>'),
        // Two fingers: two fingertips with up/down chevrons.
        D: svg('<circle cx="9" cy="12" r="2.2" fill="currentColor" stroke="none"/><circle cx="15" cy="12" r="2.2" fill="currentColor" stroke="none"/><path d="M9 5l3-3 3 3"/><path d="M9 19l3 3 3-3"/>'),
        // Thumbs: a screen split in two, a pad on the left.
        E: svg('<rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="M12 5v14"/><circle cx="7.3" cy="12" r="2.6"/>'),
        // Gyro + hold: the phone with a held fingertip on it.
        F: svg('<rect x="7" y="3" width="10" height="18" rx="2"/><circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none"/><path d="M3.5 8.5a9 9 0 0 0 0 7"/><path d="M20.5 8.5a9 9 0 0 1 0 7"/>'),
        // Hold the edge: orbit C's fingertip against the screen edge.
        G: svg('<path d="M21 4v16"/><circle cx="15.5" cy="12" r="2.6" fill="currentColor" stroke="none"/><path d="M4 12h7"/><path d="M8.5 9.5l2.5 2.5-2.5 2.5"/>')
    },
    'orbit-touch': {
        // Classic: the shipped card's rotate arrow.
        A: svg('<path d="M20 12a8 8 0 1 1-2.3-5.6"/><path d="M20 4v4h-4"/>'),
        // Gyro: the phone between motion arcs.
        B: svg('<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M3.5 8.5a9 9 0 0 0 0 7"/><path d="M20.5 8.5a9 9 0 0 1 0 7"/>'),
        // Hold the edge: a fingertip parked against the screen edge.
        C: svg('<path d="M21 4v16"/><circle cx="15.5" cy="12" r="2.6" fill="currentColor" stroke="none"/><path d="M4 12h7"/><path d="M8.5 9.5l2.5 2.5-2.5 2.5"/>')
    }
};

// Two concentric circles + eight ticks: a cog at 16px without a 40-node path.
const ICON_COG =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" aria-hidden="true">' +
    // A small "v" where the hub would be: this is the VIEWER's settings cog,
    // not the page's — a bare gear reads as generic "settings".
    '<circle cx="8" cy="8" r="5.4"/><path d="M6.2 6.3L8 9.6l1.8-3.3"/>' +
    '<path d="M8 1.3v1.3M8 13.4v1.3M14.7 8h-1.3M2.6 8H1.3M12.7 3.3l-.9.9M4.2 11.8l-.9.9M12.7 12.7l-.9-.9M4.2 4.2l-.9-.9"/>' +
    '</svg>';

const ICON_CLOSE = svg('<path d="M6 6l12 12M18 6L6 18"/>');

// Outline by default; CSS fills them when on (fill beats the attribute).
const ICON_STAR = svg('<path d="M12 3.5l2.6 5.3 5.9.9-4.25 4.1 1 5.8L12 16.9l-5.25 2.7 1-5.8L3.5 9.7l5.9-.9z"/>');
const ICON_HEART = svg('<path d="M12 20s-7-4.3-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.7-7 10-7 10z"/>');
const ICON_CHAT = svg('<path d="M4.5 5.5h15a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H10l-4 3.5v-3.5H4.5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z"/>');

/** Each language named in itself — people scan for their own language's name,
 *  not for a translation of it. */
const LANGS: ReadonlyArray<[UiLang, string]> = [['en', 'English'], ['es', 'Español'], ['ca', 'Català']];

/** The language step's heading, before anyone has chosen: every language at
 *  once ("Idioma" is both Spanish and Catalan). */
const LANG_TITLE = 'Language · Idioma';

/** How long the callout under the cog stays up after the first pick. */
const COG_TIP_MS = 5000;

/** The pick rides in the URL: resolveLang() reads ?lang= first, so everything
 *  that resolves at show time (tutorial card, toasts, loading notes) follows
 *  without being told, and a reload keeps it. */
function writeLang(lang: UiLang): void {
    try {
        const u = new URL(window.location.href);
        u.searchParams.set('lang', lang);
        history.replaceState(history.state, '', u);
    } catch { /* opaque/sandboxed origin — the language simply doesn't change */ }
}

const STYLE_ID = 'ollo-scheme-picker-css';

/** z-index budget of the ui root: chrome 15, capture/save 16–17, loading 20,
 *  diagnostics 21. The panel sits at 18/19 — over every piece of chrome it has
 *  to block, and deliberately UNDER the loading screen, so it can never punch
 *  through a loader that comes back. */
const CSS = `
.ollo-scheme-backdrop {
    position: absolute;
    inset: 0;
    z-index: 18;
    background: rgba(7, 7, 7, 0.55);
    /* A plain dim, NOT a backdrop-filter — same choice .ollo-save-backdrop
     * makes. The canvas keeps rendering behind this panel, so a full-screen
     * blur would re-filter every frame of a live WebGL scene; on the iPhone XS
     * that is exactly the wrong place to spend the GPU. The card blurs; the
     * full-bleed layer does not. */
    opacity: 0;
    transition: opacity 0.3s ease;
}
.ollo-scheme-backdrop.visible { opacity: 1; }

/* Same trap the shutter and save card hit: '.ollo-ui > *' forces pointer-events
 * back on for every direct child, so a hidden overlay silently eats input.
 * Beat it with the same specificity those overrides use. The cog callout is
 * never interactive, visible or not. */
.viewer-mount .ollo-ui > .ollo-scheme-backdrop:not(.visible),
#viewer-ui > .ollo-scheme-backdrop:not(.visible),
.viewer-mount .ollo-ui > .ollo-scheme:not(.visible),
#viewer-ui > .ollo-scheme:not(.visible),
.viewer-mount .ollo-ui > .ollo-scheme-tip,
#viewer-ui > .ollo-scheme-tip {
    pointer-events: none;
}

.ollo-scheme {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 19;
    width: min(440px, calc(100% - 32px));
    max-height: calc(100% - 32px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    border-radius: 18px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
    color: var(--white);
    font-family: var(--font-stack);
    /* A finger resting on this panel must not raise iOS text handles. */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    /* Hidden state; the reveal offset rides ON TOP of the centring transform,
     * and the collapse re-points the origin at the cog it flies to. */
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
    transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ollo-scheme.visible { opacity: 1; transform: translate(-50%, -50%) scale(1); }
.ollo-scheme.collapsing {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9) translate(28%, -34%);
}

.ollo-scheme-head { display: flex; flex-direction: column; gap: 5px; padding-right: 26px; }
.ollo-scheme-title { margin: 0; font-size: 15px; font-weight: 500; letter-spacing: 0.01em; }
.ollo-scheme-sub { margin: 0; font-size: 12px; font-weight: 300; line-height: 1.45; color: rgba(255, 255, 255, 0.6); }

.ollo-scheme-list { display: flex; flex-direction: column; gap: 8px; }
/* A step swap is a content change inside the same card, not a new card:
 * a short fade, no movement. */
.ollo-scheme > .entering { animation: ollo-scheme-step 0.22s ease; }
@keyframes ollo-scheme-step { from { opacity: 0; } to { opacity: 1; } }

.ollo-scheme-opt {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 62px;
    padding: 11px 13px;
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.045);
    border: 1px solid var(--glass-border);
    color: var(--white);
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, transform 0.12s ease;
}
.ollo-scheme-opt:hover { background: rgba(255, 255, 255, 0.09); border-color: rgba(255, 255, 255, 0.18); }
.ollo-scheme-opt:active { transform: scale(0.985); }
.ollo-scheme-opt.focused,
.ollo-scheme-opt:focus-visible {
    outline: none;
    border-color: rgba(74, 158, 255, 0.55);
    background: rgba(74, 158, 255, 0.13);
}
/* Language tiles carry one line, not three. */
.ollo-scheme-opt--lang { min-height: 50px; }

.ollo-scheme-badge {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.06em;
}
.ollo-scheme-opt.focused .ollo-scheme-badge { background: rgba(74, 158, 255, 0.3); }

.ollo-scheme-glyph { flex: 0 0 auto; width: 24px; height: 24px; opacity: 0.85; }
.ollo-scheme-glyph svg { width: 24px; height: 24px; display: block; }

.ollo-scheme-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.ollo-scheme-name { font-size: 13px; font-weight: 450; }
.ollo-scheme-desc { font-size: 11.5px; font-weight: 300; line-height: 1.35; color: rgba(255, 255, 255, 0.62); }
.ollo-scheme-note { font-size: 10.5px; font-weight: 300; line-height: 1.3; color: rgba(255, 255, 255, 0.40); }

/* The way back to the language step, under the schemes. Labelled with all
 * three names so it reads in whichever language you're stuck in. */
.ollo-scheme-langlink {
    align-self: center;
    padding: 4px 10px;
    border: none;
    border-radius: 8px;
    background: none;
    color: rgba(255, 255, 255, 0.42);
    font-family: inherit;
    font-size: 11.5px;
    font-weight: 300;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: color 0.18s ease, background 0.18s ease;
}
.ollo-scheme-langlink:hover,
.ollo-scheme-langlink:focus-visible { outline: none; color: var(--white); background: rgba(255, 255, 255, 0.07); }

/* Only the reopened panel has a way out that isn't a choice. */
.ollo-scheme-close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: color 0.2s ease, background 0.2s ease;
}
.ollo-scheme-close svg { width: 14px; height: 14px; display: block; }
.ollo-scheme-close:hover { color: var(--white); background: rgba(255, 255, 255, 0.1); }

/* The cog lives IN the toolbar row, first slot (left of share), so it gets
 * the row's own 8px gap and 36px glass buttons for free — one row, even
 * spacing, nothing measured here. It keeps its slot while hidden (opacity,
 * not display) so the row never shifts when it fades up. --floating is the
 * fallback for a mount with the toolbar switched off. */
.ollo-scheme-cog {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease, background 0.2s ease, transform 0.15s ease, border-color 0.2s ease;
}
.ollo-scheme-cog.visible { opacity: 1; pointer-events: auto; }
.ollo-scheme-cog.pointed { border-color: rgba(74, 158, 255, 0.55); }
.ollo-scheme-cog--floating {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 15;
}

/* The callout under the cog after the first pick. Placed in JS (the cog's
 * slot depends on how many toolbar buttons this mount has); --arrow-right
 * puts the arrow under the cog's centre. Near-opaque rather than glass so
 * the arrow, which can't share the blur, matches the body. */
.ollo-scheme-tip {
    position: absolute;
    z-index: 15;
    max-width: min(240px, calc(100% - 24px));
    padding: 9px 13px;
    border-radius: 10px;
    background: rgba(20, 20, 20, 0.92);
    border: 1px solid rgba(74, 158, 255, 0.55);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.4);
    color: var(--white);
    font-family: var(--font-stack);
    font-size: 12.5px;
    font-weight: 400;
    line-height: 1.35;
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.ollo-scheme-tip.visible { opacity: 1; transform: none; }
.ollo-scheme-tip::before {
    content: '';
    position: absolute;
    top: -8px;
    right: var(--arrow-right, 16px);
    width: 14px;
    height: 8px;
    background: rgba(74, 158, 255, 0.55);
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
}
.ollo-scheme-tip::after {
    content: '';
    position: absolute;
    top: -6.5px;
    right: calc(var(--arrow-right, 16px) + 1.5px);
    width: 11px;
    height: 7px;
    background: rgba(20, 20, 20, 0.92);
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
}

/* ── Feedback step ── */
.ollo-scheme-fbopen {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: rgba(74, 158, 255, 0.1);
    border: 1px solid rgba(74, 158, 255, 0.4);
    color: var(--white);
    font-family: inherit;
    font-size: 13px;
    font-weight: 450;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease;
}
.ollo-scheme-fbopen:hover,
.ollo-scheme-fbopen:focus-visible { outline: none; background: rgba(74, 158, 255, 0.2); border-color: rgba(74, 158, 255, 0.65); }
.ollo-scheme-fbopen svg { width: 18px; height: 18px; display: block; }

.ollo-scheme-fb { display: flex; flex-direction: column; gap: 10px; }
.ollo-scheme-fb-rows { display: flex; flex-direction: column; gap: 6px; }
.ollo-scheme-fb-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 6px 7px 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.045);
    border: 1px solid var(--glass-border);
    color: var(--white);
    font-family: inherit;
    text-align: left;
}
/* An untried mode: dimmed, and a tap goes and tries it. */
button.ollo-scheme-fb-row { min-height: 50px; cursor: pointer; opacity: 0.55; transition: opacity 0.18s ease, background 0.18s ease; }
button.ollo-scheme-fb-row:hover,
button.ollo-scheme-fb-row:focus-visible { outline: none; opacity: 0.9; background: rgba(255, 255, 255, 0.08); }
.ollo-scheme-fb-col { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.ollo-scheme-fb-hint { font-size: 11px; font-weight: 300; color: rgba(255, 255, 255, 0.6); }
/* -4px: the first star's hit box is wider than its drawing, so the stars
 * line up with the name above them. */
.ollo-scheme-fb-stars { display: flex; gap: 2px; margin-left: -4px; }
.ollo-scheme-fb-star,
.ollo-scheme-fb-fav {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: color 0.15s ease;
}
.ollo-scheme-fb-star { width: 30px; height: 30px; }
.ollo-scheme-fb-fav { flex: 0 0 auto; width: 40px; height: 40px; }
.ollo-scheme-fb-star svg,
.ollo-scheme-fb-fav svg { width: 20px; height: 20px; display: block; }
.ollo-scheme-fb-star.on { color: #f5c451; }
.ollo-scheme-fb-fav.on { color: #ff6b81; }
.ollo-scheme-fb-star.on svg,
.ollo-scheme-fb-fav.on svg { fill: currentColor; }
.ollo-scheme-fb-star:focus-visible,
.ollo-scheme-fb-fav:focus-visible { outline: 1px solid rgba(74, 158, 255, 0.7); }

.ollo-scheme-fb-input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 11px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--glass-border);
    color: var(--white);
    font-family: inherit;
    font-size: 13px;
    font-weight: 300;
    line-height: 1.4;
    /* The card is user-select:none; Safari won't let you type into a field
     * that inherits that. */
    -webkit-user-select: text;
    user-select: text;
    transition: border-color 0.18s ease;
}
textarea.ollo-scheme-fb-input { resize: none; min-height: 70px; }
.ollo-scheme-fb-input::placeholder { color: rgba(255, 255, 255, 0.4); }
.ollo-scheme-fb-input:focus { outline: none; border-color: rgba(74, 158, 255, 0.55); }
/* iOS zooms the whole page into any field under 16px. */
@media (pointer: coarse) { .ollo-scheme-fb-input { font-size: 16px; } }

.ollo-scheme-fb-send {
    width: 100%;
    height: 42px;
    border: none;
    border-radius: 12px;
    background: #4a9eff;
    color: #fff;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.18s ease, background 0.18s ease;
}
.ollo-scheme-fb-send:hover:not(:disabled) { background: #5aa8ff; }
.ollo-scheme-fb-send:disabled { opacity: 0.4; cursor: default; }
.ollo-scheme-fb-error { margin: 0; font-size: 11.5px; line-height: 1.4; color: #ff8a8a; }

@media (max-width: 560px) {
    .ollo-scheme { width: calc(100% - 24px); padding: 16px; gap: 12px; border-radius: 16px; }
    .ollo-scheme-opt { min-height: 58px; }
    .ollo-scheme-opt--lang { min-height: 48px; }
}

/* Phone in landscape (~375px tall) never matches the width query above and
 * would otherwise show a card taller than the viewport. */
@media (max-height: 560px) {
    .ollo-scheme { padding: 14px; gap: 10px; }
    .ollo-scheme-opt { min-height: 48px; padding: 8px 12px; }
    .ollo-scheme-opt--lang { min-height: 42px; }
    .ollo-scheme-sub { display: none; }
}

@media (prefers-reduced-motion: reduce) {
    .ollo-scheme { transition: opacity 0.2s ease; transform: translate(-50%, -50%); }
    .ollo-scheme.visible { transform: translate(-50%, -50%); }
    .ollo-scheme.collapsing { transform: translate(-50%, -50%); }
    .ollo-scheme-opt { transition: none; }
    .ollo-scheme > .entering { animation: none; }
    .ollo-scheme-tip { transition: opacity 0.2s ease; transform: none; }
}
`;

function ensureStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
}

export interface SchemePickerOptions {
    /** The viewer's ui root. */
    parent: HTMLElement;
    /** Which registry to offer — scene kind × device, each its own list. */
    registry: Registry;
    /** A scheme was chosen — apply it, then run its tutorial. Fires on every
     *  pick, including a change made later from the cog. The language is
     *  already settled by then, so the tutorial resolves the right one. */
    onChoose: (id: SchemeId) => void;
    /** Panel opened / closed. mount() uses these to pause and resume the nav
     *  controller: that is what actually kills the keyboard walk and removes
     *  the body-level joystick while the panel is up. */
    onOpen?: () => void;
    onClose?: () => void;
    /** Force touch/mouse copy. Defaults to feature detection. */
    touch?: boolean;
    /** Open the blocking panel on construction (default). False = start
     *  collapsed: the cog fades up, nothing else happens until it's clicked. */
    prompt?: boolean;
    /** Send the feedback step's answers; resolve true once stored. True shows
     *  the thank-you, false an error line. No handler → no feedback button. */
    onFeedback?: (answers: FeedbackAnswers) => Promise<boolean>;
}

type Step = 'lang' | 'scheme' | 'feedback' | 'thanks';

export class SchemePicker {

    private backdrop: HTMLDivElement;

    private root: HTMLDivElement;

    private cog: HTMLButtonElement;

    private closeBtn: HTMLButtonElement;

    private head: HTMLDivElement;

    private schemeList: HTMLDivElement;

    private langList: HTMLDivElement;

    private langLink: HTMLButtonElement;

    private tip: HTMLDivElement;

    private fbOpen: HTMLButtonElement;

    private fbWrap: HTMLDivElement;

    private fbRows: HTMLDivElement;

    private fbComment: HTMLTextAreaElement;

    private fbName: HTMLInputElement;

    private fbError: HTMLParagraphElement;

    private fbSend: HTMLButtonElement;

    private fbThanks: HTMLDivElement;

    private fbDone: HTMLButtonElement;

    private fbBack: HTMLButtonElement;

    /** Scheme tiles and language tiles; `tiles` is whichever step is up. */
    private options: HTMLButtonElement[] = [];

    private langOptions: HTMLButtonElement[] = [];

    private readonly isTouch: boolean;

    private readonly opts: SchemePickerOptions;

    /** Read per render, not once: the language step changes it live. */
    private get copy() {
        return COPY[resolveLang()];
    }

    /** The registry's tile order — every index-based lookup goes through it. */
    private readonly ids: readonly SchemeId[];

    private open = false;

    /** First open has no exit but a choice; later ones close on ×/Escape/tap. */
    private strict = true;

    private step: Step = 'lang';

    /** The language step runs on the panel's first open only. */
    private langAsked = false;

    /** The cog callout runs once, after the first (strict) pick. */
    private tipShown = false;

    /** Modes this tester has actually entered — the only ones they can rate. */
    private tried = new Set<SchemeId>();

    private ratings: Partial<Record<SchemeId, number>> = {};

    private favourite: SchemeId | null = null;

    private sending = false;

    private focusIndex = 0;

    private langFocus = 0;

    private chosen: SchemeId | null = null;

    private timers: number[] = [];

    private tipTimers: number[] = [];

    private onKeyDown: (e: KeyboardEvent) => void;

    constructor(opts: SchemePickerOptions) {
        ensureStyles();
        this.opts = opts;
        this.ids = schemeIdsFor(opts.registry);
        this.isTouch = opts.touch ?? isTouchDevice();

        this.backdrop = document.createElement('div');
        this.backdrop.className = 'ollo-scheme-backdrop';
        this.backdrop.addEventListener('click', () => { if (!this.strict) this.close(); });

        this.root = document.createElement('div');
        this.root.className = 'ollo-scheme';
        this.root.setAttribute('role', 'dialog');
        this.root.setAttribute('aria-modal', 'true');

        // Every translated string is filled by renderText() at the end of the
        // constructor, and again once a language is picked.
        this.head = document.createElement('div');
        this.head.className = 'ollo-scheme-head';
        this.root.appendChild(this.head);

        this.langList = document.createElement('div');
        this.langList.className = 'ollo-scheme-list';
        LANGS.forEach(([lang, name], i) => {
            const b = document.createElement('button');
            b.className = 'ollo-scheme-opt ollo-scheme-opt--lang';
            b.type = 'button';
            b.dataset.lang = lang;
            b.lang = lang;
            b.innerHTML =
                `<span class="ollo-scheme-badge">${lang.toUpperCase()}</span>` +
                `<span class="ollo-scheme-name">${name}</span>`;
            b.addEventListener('click', (e) => { e.stopPropagation(); this.pickLang(lang); });
            b.addEventListener('pointerenter', () => this.setFocus(i));
            this.langList.appendChild(b);
            this.langOptions.push(b);
        });
        this.root.appendChild(this.langList);

        this.schemeList = document.createElement('div');
        this.schemeList.className = 'ollo-scheme-list';
        this.ids.forEach((id, i) => {
            const b = document.createElement('button');
            b.className = 'ollo-scheme-opt';
            b.type = 'button';
            b.dataset.scheme = id;
            b.addEventListener('click', (e) => { e.stopPropagation(); this.choose(id); });
            // Pointer focus follows the mouse so the keyboard highlight never
            // disagrees with what the cursor is over.
            b.addEventListener('pointerenter', () => this.setFocus(i));
            this.schemeList.appendChild(b);
            this.options.push(b);
        });
        this.root.appendChild(this.schemeList);

        // Feedback: a button under the schemes opens a third step in the same
        // card (setStep decides when the button is offered).
        this.fbOpen = document.createElement('button');
        this.fbOpen.className = 'ollo-scheme-fbopen';
        this.fbOpen.type = 'button';
        this.fbOpen.addEventListener('click', (e) => { e.stopPropagation(); this.setStep('feedback'); });
        this.root.appendChild(this.fbOpen);

        this.fbWrap = document.createElement('div');
        this.fbWrap.className = 'ollo-scheme-fb';
        this.fbRows = document.createElement('div');
        this.fbRows.className = 'ollo-scheme-fb-rows';
        // One delegated listener: the rows are rebuilt on every language render.
        this.fbRows.addEventListener('click', (e) => {
            e.stopPropagation();
            const el = (e.target as HTMLElement).closest<HTMLElement>('[data-star],[data-fav],[data-go]');
            if (!el) return;
            if (el.dataset.go) { this.choose(el.dataset.go as SchemeId); return; }
            if (el.dataset.fav) {
                const id = el.dataset.fav as SchemeId;
                this.favourite = this.favourite === id ? null : id;
            } else {
                const [id, n] = el.dataset.star!.split(':') as [SchemeId, string];
                // Tapping the current rating again clears it.
                this.ratings[id] = this.ratings[id] === Number(n) ? 0 : Number(n);
            }
            this.paintFeedback();
        });
        this.fbComment = document.createElement('textarea');
        this.fbComment.className = 'ollo-scheme-fb-input';
        this.fbComment.rows = 3;
        this.fbComment.maxLength = 2000;
        this.fbComment.addEventListener('input', () => this.updateSend());
        this.fbName = document.createElement('input');
        this.fbName.className = 'ollo-scheme-fb-input';
        this.fbName.type = 'text';
        this.fbName.maxLength = 80;
        this.fbName.autocomplete = 'name';
        this.fbError = document.createElement('p');
        this.fbError.className = 'ollo-scheme-fb-error';
        this.fbError.setAttribute('role', 'alert');
        this.fbError.style.display = 'none';
        this.fbSend = document.createElement('button');
        this.fbSend.className = 'ollo-scheme-fb-send';
        this.fbSend.type = 'button';
        this.fbSend.addEventListener('click', (e) => { e.stopPropagation(); void this.sendFeedback(); });
        this.fbWrap.append(this.fbRows, this.fbComment, this.fbName, this.fbError, this.fbSend);
        this.root.appendChild(this.fbWrap);

        this.fbThanks = document.createElement('div');
        this.fbThanks.className = 'ollo-scheme-fb';
        this.fbDone = document.createElement('button');
        this.fbDone.className = 'ollo-scheme-fb-send';
        this.fbDone.type = 'button';
        this.fbDone.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
        this.fbThanks.appendChild(this.fbDone);
        this.root.appendChild(this.fbThanks);

        this.fbBack = document.createElement('button');
        this.fbBack.className = 'ollo-scheme-langlink';
        this.fbBack.type = 'button';
        this.fbBack.addEventListener('click', (e) => { e.stopPropagation(); this.setStep('scheme'); });
        this.root.appendChild(this.fbBack);

        this.langLink = document.createElement('button');
        this.langLink.className = 'ollo-scheme-langlink';
        this.langLink.type = 'button';
        this.langLink.textContent = '‹ ' + LANGS.map(([, name]) => name).join(' · ');
        this.langLink.addEventListener('click', (e) => { e.stopPropagation(); this.setStep('lang'); });
        this.root.appendChild(this.langLink);

        const close = document.createElement('button');
        close.className = 'ollo-scheme-close';
        close.type = 'button';
        close.innerHTML = ICON_CLOSE;
        close.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
        close.style.display = 'none';   // shown from the second open onward
        this.root.appendChild(close);
        this.closeBtn = close;

        this.cog = document.createElement('button');
        this.cog.className = 'ollo-btn ollo-toolbar-btn ollo-scheme-cog';
        this.cog.type = 'button';
        this.cog.innerHTML = ICON_COG;
        this.cog.addEventListener('click', () => this.reopen());

        this.tip = document.createElement('div');
        this.tip.className = 'ollo-scheme-tip';
        this.tip.setAttribute('role', 'status');

        opts.parent.appendChild(this.backdrop);
        opts.parent.appendChild(this.root);
        opts.parent.appendChild(this.tip);
        // Into the toolbar row, first slot — left of share — so it sits in one
        // row with the other icons at the row's own spacing. Looked up by
        // class rather than handed in: Toolbar stays untouched and the whole
        // experiment still deletes as one folder. No toolbar (ui.toolbar off)
        // → float it where the row would have been.
        const toolbar = opts.parent.querySelector<HTMLElement>('.ollo-toolbar');
        if (toolbar) {
            toolbar.insertBefore(this.cog, toolbar.firstChild);
        } else {
            this.cog.classList.add('ollo-scheme-cog--floating');
            opts.parent.appendChild(this.cog);
        }
        this.renderText();

        // Keys reach the walk controller through WINDOW listeners, which no
        // overlay can cover — swallow them at capture while the panel is up,
        // and spend them on the panel instead.
        this.onKeyDown = (e: KeyboardEvent) => this.handleKey(e);
        window.addEventListener('keydown', this.onKeyDown, true);

        // Pre-highlight the last pick (returning tester) or the control arm.
        // The saved pick may belong to the OTHER registry (walk E on an orbit
        // scene) — indexOf then misses and the max() lands on the control arm.
        // The language step pre-highlights whatever the viewer resolved.
        const initial = savedScheme() ?? DEFAULT_SCHEME;
        this.focusIndex = Math.max(0, this.ids.indexOf(initial));
        this.langFocus = Math.max(0, LANGS.findIndex(([l]) => l === resolveLang()));
        if (opts.prompt ?? true) {
            this.show(true);
        } else {
            // Cog mode: the control arm is already in force (mount applies
            // it), so there is nothing to decide yet — no backdrop, no
            // panel, and the first open is already the dismissible kind.
            this.strict = false;
            this.chosen = DEFAULT_SCHEME;
            this.tried.add(DEFAULT_SCHEME);
            this.timers.push(window.setTimeout(() => this.cog.classList.add('visible'), 400));
        }
    }

    // ── Open / close ──────────────────────────────────────────────────────
    private show(strict: boolean): void {
        this.strict = strict;
        this.open = true;
        this.closeBtn.style.display = strict ? 'none' : 'flex';
        this.setStep(this.langAsked ? 'scheme' : 'lang', false);
        this.root.classList.remove('collapsing');
        // Force a style computation so the hidden state is the transition's
        // from-value (rAF runs before style recalc and would skip the fade).
        void this.root.offsetWidth;
        this.backdrop.classList.add('visible');
        this.root.classList.add('visible');
        this.opts.onOpen?.();
    }

    private reopen(): void {
        if (this.open) return;
        this.hideTip();
        this.cog.classList.remove('visible');
        this.show(false);
    }

    /** Leave without changing anything — only reachable after a first choice. */
    private close(): void {
        if (!this.open || this.strict) return;
        this.open = false;
        this.backdrop.classList.remove('visible');
        this.root.classList.remove('visible');
        this.cog.classList.add('visible');
        this.opts.onClose?.();
    }

    private choose(id: SchemeId): void {
        if (!this.open) return;
        const first = this.strict && !this.tipShown;
        this.open = false;
        this.chosen = id;
        this.tried.add(id);
        saveScheme(id);
        this.focusIndex = Math.max(0, this.ids.indexOf(id));
        // Collapse INTO the cog: the card flies at the corner the cog occupies
        // while the cog fades up underneath it, so the button is visibly where
        // the panel went rather than a new thing that appeared.
        this.backdrop.classList.remove('visible');
        this.root.classList.remove('visible');
        this.root.classList.add('collapsing');
        this.timers.push(window.setTimeout(() => this.cog.classList.add('visible'), 120));
        // First pick from the blocking panel: nobody has seen the cog yet, so
        // point at it once the collapse has landed. (Cog mode needs no
        // callout — its tester found the panel BY the cog.)
        if (first) {
            this.tipShown = true;
            this.timers.push(window.setTimeout(() => this.showTip(), 450));
        }
        // onClose (nav resumes, controller re-attaches) BEFORE onChoose
        // (applyScheme): once the schemes really reconfigure the controller,
        // they must run on an ATTACHED one, or attach() would undo them.
        this.opts.onClose?.();
        this.opts.onChoose(id);
    }

    // ── Steps + language ──────────────────────────────────────────────────
    private setStep(step: Step, animate = true): void {
        this.step = step;
        const show = (el: HTMLElement, on: boolean) => { el.style.display = on ? '' : 'none'; };
        show(this.langList, step === 'lang');
        show(this.schemeList, step === 'scheme');
        // Nothing to rate until a mode has been entered — so never on the
        // blocking first panel.
        show(this.fbOpen, step === 'scheme' && !!this.opts.onFeedback && this.tried.size > 0);
        show(this.langLink, step === 'scheme');
        show(this.fbWrap, step === 'feedback');
        show(this.fbBack, step === 'feedback');
        show(this.fbThanks, step === 'thanks');
        if (step === 'feedback') {
            this.fbError.style.display = 'none';
            this.renderFeedback();
        }
        this.renderHead();
        const body = { lang: this.langList, scheme: this.schemeList, feedback: this.fbWrap, thanks: this.fbThanks }[step];
        body.classList.remove('entering');
        if (animate) {
            void body.offsetWidth;
            body.classList.add('entering');
        }
        if (step === 'lang' || step === 'scheme') this.setFocus(step === 'lang' ? this.langFocus : this.focusIndex);
        this.root.scrollTop = 0;
    }

    private pickLang(lang: UiLang): void {
        if (!this.open) return;
        this.langAsked = true;
        this.langFocus = Math.max(0, LANGS.findIndex(([l]) => l === lang));
        if (lang !== resolveLang()) writeLang(lang);
        this.renderText();
        this.setStep('scheme');
    }

    // ── Feedback ──────────────────────────────────────────────────────────
    /** Rows in registry order: a tried mode gets stars and a ♥, an untried
     *  one is a dimmed row that switches to it. */
    private renderFeedback(): void {
        const c = this.copy;
        const reg = this.opts.registry;
        this.fbRows.innerHTML = this.ids.map((id) => {
            const name = c.schemes[reg][id]!.name;
            const badge = `<span class="ollo-scheme-badge">${id}</span>`;
            if (!this.tried.has(id)) {
                return `<button type="button" class="ollo-scheme-fb-row" data-go="${id}">${badge}` +
                    `<span class="ollo-scheme-fb-col"><span class="ollo-scheme-name">${name}</span>` +
                    `<span class="ollo-scheme-fb-hint">${c.feedback.notTried}</span></span></button>`;
            }
            const stars = [1, 2, 3, 4, 5].map((n) =>
                `<button type="button" class="ollo-scheme-fb-star" data-star="${id}:${n}" aria-label="${name}: ${n}/5">${ICON_STAR}</button>`
            ).join('');
            return `<div class="ollo-scheme-fb-row">${badge}` +
                `<span class="ollo-scheme-fb-col"><span class="ollo-scheme-name">${name}</span>` +
                `<span class="ollo-scheme-fb-stars">${stars}</span></span>` +
                `<button type="button" class="ollo-scheme-fb-fav" data-fav="${id}" aria-label="${c.feedback.favourite}: ${name}">${ICON_HEART}</button>` +
                '</div>';
        }).join('');
        this.paintFeedback();
    }

    /** Stars and hearts follow the answers; the rows themselves stay put so
     *  keyboard focus survives a click. */
    private paintFeedback(): void {
        this.fbRows.querySelectorAll<HTMLButtonElement>('[data-star]').forEach((b) => {
            const [id, n] = b.dataset.star!.split(':');
            const on = (this.ratings[id as SchemeId] ?? 0) >= Number(n);
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', String(on));
        });
        this.fbRows.querySelectorAll<HTMLButtonElement>('[data-fav]').forEach((b) => {
            const on = b.dataset.fav === this.favourite;
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', String(on));
        });
        this.updateSend();
    }

    /** Send needs something to send: a star, a ♥ or a word. */
    private updateSend(): void {
        const any = Object.values(this.ratings).some((n) => (n ?? 0) > 0)
            || this.favourite !== null || this.fbComment.value.trim() !== '';
        this.fbSend.disabled = this.sending || !any;
    }

    private async sendFeedback(): Promise<void> {
        if (this.sending || !this.opts.onFeedback) return;
        this.sending = true;
        this.fbError.style.display = 'none';
        this.fbSend.textContent = this.copy.feedback.sending;
        this.updateSend();
        const tried = this.ids.filter((id) => this.tried.has(id));
        const ratings: Partial<Record<SchemeId, number>> = {};
        for (const id of tried) {
            const n = this.ratings[id] ?? 0;
            if (n > 0) ratings[id] = n;
        }
        let ok = false;
        try {
            ok = await this.opts.onFeedback({
                registry: this.opts.registry,
                lang: resolveLang(),
                tried,
                ratings,
                favourite: this.favourite,
                comment: this.fbComment.value.trim(),
                name: this.fbName.value.trim()
            });
        } catch {
            ok = false;
        }
        this.sending = false;
        this.fbSend.textContent = this.copy.feedback.send;
        this.updateSend();
        if (!this.open || this.step !== 'feedback') return;
        if (ok) this.setStep('thanks');
        else this.fbError.style.display = '';
    }

    // ── Cog callout ───────────────────────────────────────────────────────
    private showTip(): void {
        this.tip.textContent = this.copy.cogHint;
        // Measured, not fixed: the cog's slot depends on how many toolbar
        // buttons this mount has. Right-anchored so a long translation grows
        // leftward into the scene instead of off the screen edge.
        const pr = this.opts.parent.getBoundingClientRect();
        const cr = this.cog.getBoundingClientRect();
        const fromRight = pr.right - (cr.left + cr.width / 2);   // cog centre
        const right = Math.max(12, fromRight - 30);
        this.tip.style.top = `${cr.bottom - pr.top + 10}px`;
        this.tip.style.right = `${right}px`;
        this.tip.style.setProperty('--arrow-right', `${Math.max(8, fromRight - right - 7)}px`);
        void this.tip.offsetWidth;
        this.tip.classList.add('visible');
        this.cog.classList.add('pointed');
        this.tipTimers.push(window.setTimeout(() => this.hideTip(), COG_TIP_MS));
    }

    private hideTip(): void {
        for (const t of this.tipTimers) clearTimeout(t);
        this.tipTimers = [];
        this.tip.classList.remove('visible');
        this.cog.classList.remove('pointed');
    }

    // ── Keyboard ──────────────────────────────────────────────────────────
    private handleKey(e: KeyboardEvent): void {
        if (!this.open) return;
        // Tab stays alive so the panel is still keyboard-navigable; everything
        // else is consumed, or WASD would walk the camera behind the panel.
        if (e.key === 'Tab') return;
        const k = e.key.toLowerCase();
        // Typing in a feedback field, working a button that isn't a tile
        // (stars, ♥, send, the footer links), or anywhere on the feedback
        // steps: keep the key from the viewer's window shortcuts but let the
        // browser do its default — type the letter, press the button.
        // Escape still leaves.
        const t = e.target;
        const editable = t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement;
        const otherButton = t instanceof HTMLButtonElement && !this.tiles.includes(t) && (k === 'enter' || k === ' ');
        if (editable || otherButton || this.step === 'feedback' || this.step === 'thanks') {
            e.stopPropagation();
            if (k === 'escape') {
                e.preventDefault();
                this.close();
            }
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const n = this.tiles.length;
        const pick = (i: number) => {
            if (this.step === 'lang') this.pickLang(LANGS[i][0]);
            else this.choose(this.ids[i]);
        };
        if (k === 'escape') {
            // Strict = no exit without a decision, so Escape ACCEPTS the
            // highlighted option rather than trapping the user.
            if (this.strict) pick(this.currentFocus);
            else this.close();
            return;
        }
        if (k === 'enter' || k === ' ') { pick(this.currentFocus); return; }
        if (k === 'arrowdown' || k === 'arrowright') { this.setFocus(this.currentFocus + 1); return; }
        if (k === 'arrowup' || k === 'arrowleft') { this.setFocus(this.currentFocus - 1); return; }
        // Direct pick: 1–7, or a–g on the scheme step (the language names
        // share initials, so letters mean nothing there), capped to the tiles.
        const byNum = '1234567'.indexOf(k);
        if (byNum >= 0 && byNum < n) { pick(byNum); return; }
        const byLetter = 'abcdefg'.indexOf(k);
        if (this.step === 'scheme' && byLetter >= 0 && byLetter < n) pick(byLetter);
    }

    // ── Text ──────────────────────────────────────────────────────────────
    private renderHead(): void {
        if (this.step === 'feedback' || this.step === 'thanks') {
            const f = this.copy.feedback;
            const [title, sub] = this.step === 'feedback' ? [f.title, f.sub] : [f.thanks, f.thanksSub];
            this.root.setAttribute('aria-label', title);
            this.head.innerHTML =
                `<h2 class="ollo-scheme-title">${title}</h2>` +
                `<p class="ollo-scheme-sub">${sub}</p>`;
            return;
        }
        if (this.step === 'lang') {
            this.root.setAttribute('aria-label', LANG_TITLE);
            this.head.innerHTML = `<h2 class="ollo-scheme-title">${LANG_TITLE}</h2>`;
            return;
        }
        const c = this.copy;
        this.root.setAttribute('aria-label', c.title);
        this.head.innerHTML =
            `<h2 class="ollo-scheme-title">${c.title}</h2>` +
            `<p class="ollo-scheme-sub">${c.subtitle[this.opts.registry]}</p>`;
    }

    /** Every translated string in the panel and the cog. Buttons keep their
     *  listeners; only their contents are rewritten. */
    private renderText(): void {
        const c = this.copy;
        const reg = this.opts.registry;
        this.renderHead();
        this.options.forEach((b, i) => {
            const id = this.ids[i];
            // The ids list and the copy/glyph tables are hand-kept in sync;
            // the ! is the registry's own contract.
            const s = c.schemes[reg][id]!;
            b.innerHTML =
                `<span class="ollo-scheme-badge">${id}</span>` +
                `<span class="ollo-scheme-glyph">${GLYPH[reg][id]!}</span>` +
                '<span class="ollo-scheme-text">' +
                    `<span class="ollo-scheme-name">${s.name}</span>` +
                    `<span class="ollo-scheme-desc">${this.isTouch ? s.touch : s.mouse}</span>` +
                    // Each registry's notes are written for its own device,
                    // so they always show.
                    (s.note ? `<span class="ollo-scheme-note">${s.note}</span>` : '') +
                '</span>';
        });
        this.closeBtn.setAttribute('aria-label', c.close);
        this.langLink.setAttribute('aria-label', c.language);
        this.cog.title = c.change;
        this.cog.setAttribute('aria-label', c.change);
        const f = c.feedback;
        this.fbOpen.innerHTML = `${ICON_CHAT}<span>${f.open}</span>`;
        this.fbComment.placeholder = f.comment;
        this.fbComment.setAttribute('aria-label', f.comment);
        this.fbName.placeholder = f.name;
        this.fbName.setAttribute('aria-label', f.name);
        this.fbSend.textContent = this.sending ? f.sending : f.send;
        this.fbError.textContent = f.error;
        this.fbBack.textContent = '‹ ' + f.back;
        this.fbDone.textContent = f.done;
    }

    private get tiles(): HTMLButtonElement[] {
        return this.step === 'lang' ? this.langOptions : this.options;
    }

    private get currentFocus(): number {
        return this.step === 'lang' ? this.langFocus : this.focusIndex;
    }

    private setFocus(i: number): void {
        const tiles = this.tiles;
        const n = tiles.length;
        const f = ((i % n) + n) % n;
        if (this.step === 'lang') this.langFocus = f;
        else this.focusIndex = f;
        tiles.forEach((b, k) => b.classList.toggle('focused', k === f));
    }

    /** The scheme in force, or null while nobody has chosen yet. */
    get scheme(): SchemeId | null {
        return this.chosen;
    }

    dispose(): void {
        for (const t of this.timers) clearTimeout(t);
        this.timers = [];
        this.hideTip();
        window.removeEventListener('keydown', this.onKeyDown, true);
        this.backdrop.remove();
        this.root.remove();
        this.cog.remove();
        this.tip.remove();
    }
}
